import store from 'store';
import { showNotification } from 'actions/ui';
import { NotificationTypes } from 'constants';

import reporter from '../monitoring/errorReporter';
import { installErrorHandlers } from '../utils/errorHandle';

// встановлюємо глобальні хендлери одразу при імпорті
var axiosInstance = (typeof window !== 'undefined') ? window.axios : undefined;
installErrorHandlers({ reporter: reporter, axios: axiosInstance, captureConsole: true });

// Уніфікація полів з RTK Query / axios-помилки
function extractErrorInfo(action) {
	return {
		code: (action && action.error && action.error.code) || (action && action.payload && action.payload.code),
		message:
			(action && action.error && action.error.message) ||
			(action && action.payload && (action.payload.errorMessage || action.payload.message)) ||
			'Request error',
		status: (action && action.payload && action.payload.status) || (action && action.error && action.error.status),
		endpoint: action && action.meta && action.meta.arg ? action.meta.arg.endpointName : undefined,
		url:
			(action && action.meta && action.meta.baseQueryMeta && action.meta.baseQueryMeta.request && action.meta.baseQueryMeta.request.url) ||
			(action && action.meta && action.meta.arg && action.meta.arg.originalArgs && action.meta.arg.originalArgs.url) ||
			undefined,
		method:
			(action && action.meta && action.meta.baseQueryMeta && action.meta.baseQueryMeta.request && action.meta.baseQueryMeta.request.method) ||
			(action && action.meta && action.meta.arg && action.meta.arg.originalArgs && action.meta.arg.originalArgs.method) ||
			undefined,
		response: (action && action.payload && action.payload.data) || (action && action.payload),
		requestId: action && action.meta ? action.meta.requestId : undefined
	};
}

function shouldIncludeResponseBodies() {
	try {
		return Boolean(typeof window !== 'undefined' && window.__RUM_CONFIG__ && window.__RUM_CONFIG__.includeResponseBodies);
	} catch (e) {
		return false;
	}
}

function sanitizeResponsePayload(response) {
	if (!response || typeof response !== 'object') {
		return response;
	}

	// Prefer typical crm-api payload shape, but keep it generic.
	var payload = (response && response.data && typeof response.data === 'object') ? response.data : response;
	var out = {};
	var keys = [
		'success',
		'errorCode',
		'errorMessage',
		'errorMessageText',
		'message',
		'status',
		'sessionId',
		'transactionId',
		'timestamp',
	];
	keys.forEach(function (k) {
		if (payload[k] !== undefined) {
			out[k] = payload[k];
		}
	});
	if (payload.errors !== undefined) {
		out.errors = payload.errors;
	}
	return out;
}

function shouldIgnoreRtkQueryError(info) {
	// This case is informational for the UI: the appeal is simply locked by another user.
	// It should still show a notification, but should not be sent to backend logging.
	if (!info || info.endpoint !== 'appealLock') {
		return false;
	}

	var response = info.response;
	var errorCode = response && response.errorCode;
	if (Number(errorCode) !== -20001) {
		return false;
	}

	var errorMessage = (response && (response.errorMessage || response.errorMessageText)) || info.message;
	return /Звернення заблоковано користувачем/i.test(String(errorMessage || ''));
}

function isSilentSkippedSaveError(info) {
	if (!info || info.endpoint !== 'interactionAppealSave') {
		return false;
	}

	var response = info.response;
	if (!response || response.success !== false) {
		return false;
	}

	var skipped = response.result && response.result.skipped === true;
	var reason = response.result && response.result.reason;
	return skipped && ['duplicate_payload', 'incomplete_payload', 'mutex_busy'].includes(reason);
}

function isAppealAccessDeniedError(info) {
	if (!info) {
		return false;
	}

	var response = info.response;
	var errorCode = Number(response && response.errorCode);
	if (errorCode === -20403) {
		return true;
	}

	var errorMessage = (response && (response.errorMessage || response.errorMessageText || response.message)) || info.message;
	return /ORA-20403|code=-?20403|відсутні права на перегляд/i.test(String(errorMessage || ''));
}

function shouldSuppressRtkQueryNotification(info) {
	if (isSilentSkippedSaveError(info)) {
		return true;
	}

	if (!isAppealAccessDeniedError(info)) {
		return false;
	}

	return ['appeal', 'appealQuery'].includes(info.endpoint);
}

/**
 * RTK Query middleware:
 *  - зберігає нотифікацію
 *  - ДОДАТКОВО шле діагностику на бекенд
 */
export const rtkQueryErrorLogger = () => (next) => (action) => {
	var isBadResponse = action && action.error && action.error.code === 'ERR_BAD_RESPONSE';
	var isFailedPayload = action && action.payload && action.payload.success === false;

	if (isBadResponse || isFailedPayload) {
		var info;
		try {
			info = extractErrorInfo(action);
		} catch (e) {}
		var shouldSuppressNotification = shouldSuppressRtkQueryNotification(info);

		// 1) Нотифікація
		if (!shouldSuppressNotification) {
			store.dispatch(
				showNotification({
					type: NotificationTypes.HTTP_ERROR,
					options: {
						message:
							(action && action.error && action.error.message) ||
							(action && action.payload && (action.payload.errorMessage || action.payload.message)),
					},
				})
			);
		}

		// 2) Звіт на бек
		try {
			if (!shouldIgnoreRtkQueryError(info) && !shouldSuppressNotification && !isSilentSkippedSaveError(info)) {
				var responsePayload = shouldIncludeResponseBodies()
					? info.response
					: sanitizeResponsePayload(info.response);
				reporter.captureMessage('rtk-query-error', {
					source: 'rtk-query',
					code: info.code,
					message: info.message,
					status: info.status,
					endpoint: info.endpoint,
					url: info.url,
					method: info.method,
					response: responsePayload,
					requestId: info.requestId
				});
			}
		} catch (e) {}
	}

	return next(action);
};

export const reportError = function (error, custom) {
	reporter.captureError(error, custom || {});
};

export const reportMessage = function (message, custom) {
	reporter.captureMessage(message, custom || {});
};

export default reporter;
